import type { ColumnDef } from "@tanstack/react-table";
import { t } from "i18next";

import type { ICvssVersion } from "./types";

import { getSeverity } from "../assigned-vulnerabilities/utils";
import { statusFormatter } from "features/vulnerabilities/formatters/status";
import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { filterDate } from "utils/date";
import { LocationFormatter, formatLinkHandler } from "utils/format-helpers";
import { severityFormatter } from "utils/format-helpers";

const getColumns = (cvssVersion: ICvssVersion): ColumnDef<IVulnRowAttr>[] => [
  {
    accessorKey: "organizationName",
    cell: (cell): JSX.Element => {
      const orgName = cell.row.original.organizationName ?? "";
      const link = `/orgs/${orgName}/groups/${cell.row.original.groupName}`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    header: t("searchFindings.tabVuln.vulnTable.organization"),
  },
  {
    accessorKey: "where",
    cell: LocationFormatter,
    enableColumnFilter: false,
    header: t("searchFindings.tabVuln.vulnTable.where"),
    size: 250,
  },
  {
    accessorKey: "specific",
    enableColumnFilter: false,
    header: t("searchFindings.tabVuln.vulnTable.specific"),
    size: 70,
    sortingFn: "alphanumeric",
  },
  {
    accessorKey: "groupName",
    cell: (cell): JSX.Element => {
      const orgName = cell.row.original.organizationName ?? "";
      const link = `/orgs/${orgName}/groups/${cell.row.original.groupName}`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    header: t("organization.tabs.groups.newGroup.name.text"),
    size: 70,
  },
  {
    accessorFn: (row): string | undefined => row.finding?.title,
    cell: (cell): JSX.Element => {
      const orgName = cell.row.original.organizationName ?? "";
      const findingId =
        cell.row.original.finding === undefined
          ? ""
          : cell.row.original.finding.id;
      const link =
        `/orgs/${orgName}/groups/${cell.row.original.groupName}` +
        `/vulns/${findingId}/locations`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    header: t("searchFindings.tabVuln.vulnTable.vulnerabilityType.title"),
    id: "finding-title",
    size: 250,
  },
  {
    accessorKey: "state",
    cell: (cell): JSX.Element => {
      const labels: Record<string, string> = {
        REJECTED: t("searchFindings.tabVuln.rejected"),
        SAFE: t("searchFindings.tabVuln.closed"),
        SUBMITTED: t("searchFindings.tabVuln.submitted"),
        VULNERABLE: t("searchFindings.tabVuln.open"),
      };

      return statusFormatter(labels[cell.getValue<string>()]);
    },
    header: t("searchFindings.tabVuln.vulnTable.status"),
    size: 50,
  },
  {
    accessorKey: "reportDate",
    filterFn: filterDate,
    header: t("searchFindings.tabVuln.vulnTable.reportDate"),
  },
  {
    accessorFn: (): string => "View",
    cell: (cell): JSX.Element => {
      const orgName = cell.row.original.organizationName ?? "";
      const findingId =
        cell.row.original.finding === undefined
          ? ""
          : cell.row.original.finding.id;
      const link =
        `/orgs/${orgName}/groups/${cell.row.original.groupName}` +
        `/vulns/${findingId}/evidence`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    enableColumnFilter: false,
    header: "Evidence",
    id: "evidence",
    size: 50,
  },
  {
    accessorFn: (row): number => getSeverity(cvssVersion, row),
    cell: (cell): JSX.Element => severityFormatter(Number(cell.getValue())),
    header: cvssVersion.header,
    id: "severity",
    size: 120,
  },
  {
    accessorKey: "hacker",
    enableColumnFilter: false,
    header: t("searchFindings.tabVuln.vulnTable.hacker"),
    size: 250,
  },
];

export { getColumns };
